import { createApi } from '@reduxjs/toolkit/query/react'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { limitFunction } from 'p-limit'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../../../../store/models'
import { User } from '../../../../models/User'
import { Room, RoomSession } from '../../../../models/Room'
import { Task } from '../../../../models/MeetingSupport'
import { baseQueryWithTokenRefresh } from '../../../../common/baseQuery'
import { SessionFilter, SessionSort } from '../../../../components/SessionFilters/model'
import { paginate } from '../../../../store/utils'

const _BASE_URL = import.meta.env.REACT_APP_API_BASE_URL!
const _MEETING_SUPPORT_BASE_URL = import.meta.env.REACT_APP_MEETING_SUPPORT_BASE_URL!

export interface GetUserRoomSessionsRequest extends BasePageRequest {
  userId?: string
  startedAfter?: number
  startedBefore?: number
  participantId?: string
}

export interface GetRoomSessionsByFilterRequest extends BasePageRequest {
  filter?: SessionFilter
  filterId?: string
  sort?: SessionSort
  term?: string
}

export interface GetRoomRequest extends BaseRequest {
  roomId: string
}

export interface DeleteRoomSessionRequest extends BaseRequest {
  roomId: string
  sessionId: string
}

export interface LiveStatus {
  hasLiveSession: boolean
}

export interface GetClientsPreviousMeetingTasksRequest extends BaseRequest {
  clients: User[]
}

export interface ClientPreviousMeetingTasks {
  clientId: string
  roomSession: RoomSession
  tasks: Task[]
}

export interface GroupedUserTasks {
  user: User
  tasks: Task[]
}

export const faApi = createApi({
  reducerPath: 'faApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Session', 'Tasks'],
  endpoints: (builder) => ({
    getRoom: builder.query<Room, GetRoomRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}`,
        }
      },
      transformResponse: (response: BaseResponse<Room>) => {
        return response.data
      },
      providesTags: ['Session'],
    }),
    getUserRoomSessions: builder.query<BasePageResponse<RoomSession[]>, GetUserRoomSessionsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/`,
          params: {
            userId: request.userId,
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            startedAfter: request.startedAfter,
            startedBefore: request.startedBefore,
            participantId: request.participantId,
          },
        }
      },
      providesTags: ['Session'],
    }),
    getRoomSessionsForUser: builder.query<BasePageResponse<RoomSession[]>, GetUserRoomSessionsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/list-for-user`,
          method: 'POST',
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
          },
          body: {
            userId: request.userId,
            startedAfter: request.startedAfter,
            startedBefore: request.startedBefore,
            participantId: request.participantId,
          },
        }
      },
      providesTags: ['Session'],
    }),
    getRoomSessionsForFilter: builder.query<BasePageResponse<RoomSession[]>, GetRoomSessionsByFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/filter`,
          method: 'POST',
          body: {
            filter: request.filter,
            filterId: request.filterId,
            sort: request.sort,
            term: request.term,
          },
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
          },
        }
      },
      providesTags: ['Session'],
    }),
    getLiveStatus: builder.query<BaseResponse<LiveStatus>, BaseRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/live-status`,
          method: 'GET',
        }
      },
    }),
    deleteRoomSession: builder.mutation<BaseResponse<User>, DeleteRoomSessionRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Session'],
    }),
    getPhoneCandidates: builder.query<BasePageResponse<RoomSession[]>, BasePageRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions/phone-candidates`,
          method: 'GET',
        }
      },
    }),
    getClientsPreviousMeetingTasks: builder.query<GroupedUserTasks[], GetClientsPreviousMeetingTasksRequest>({
      async queryFn(request, _queryApi, _extraOptions, _fetchWithBQ) {
        const fetchWithBQ = limitFunction(async (...args: Parameters<typeof _fetchWithBQ>) => _fetchWithBQ(...args), { concurrency: 10 })
        const { accountId, clients } = request

        if (clients.length === 0) {
          return { data: [] }
        }

        try {
          const sessionResults = await Promise.all(
            clients.map(async (client) => {
              const result = await fetchWithBQ({
                url: `/accounts/${accountId}/sessions/`,
                params: {
                  participantId: client.id,
                  page: 0,
                  size: 1,
                },
              })

              if (result.error) {
                return { clientId: client.id, error: result.error }
              }

              const response = result.data as BasePageResponse<RoomSession[]>
              if (response?.data && response.data.length > 0) {
                return { clientId: client.id, roomSession: response.data[0] }
              }

              return { clientId: client.id, roomSession: null }
            }),
          )

          const clientsWithSessions = sessionResults.filter(
            (result): result is { clientId: string; roomSession: RoomSession } => result.roomSession != null && !('error' in result),
          )

          if (clientsWithSessions.length === 0) {
            return { data: [] }
          }

          const uniqueSessions = [...new Map(clientsWithSessions.map(({ roomSession }) => [roomSession.id, roomSession])).values()]
          const clientIds = new Set(clients.map((client) => client.id))

          const tasksByUserId = new Map<string, { user: User; tasks: Task[] }>()

          await Promise.all(
            uniqueSessions.map(async (roomSession) => {
              const result = await paginate<Task>(
                {
                  url: `${_MEETING_SUPPORT_BASE_URL}/accounts/${accountId}/tasks`,
                  params: {
                    type: 'meeting',
                    status: 'open',
                    roomId: roomSession.roomId,
                    sessionId: roomSession.id,
                  },
                },
                fetchWithBQ,
              )

              if (result.error) {
                return
              }

              const tasks = result.data.data

              for (const task of tasks) {
                const assignedToUser = task.assignedTo?.user
                if (!assignedToUser) {
                  continue
                }

                const relatedToUser = task.relatedTo?.user

                const isAssignedToClient = clientIds.has(assignedToUser.id)
                const isRelatedToClient = relatedToUser != null && clientIds.has(relatedToUser.id)

                if (!isAssignedToClient && !isRelatedToClient) {
                  continue
                }

                const existing = tasksByUserId.get(assignedToUser.id)
                if (existing) {
                  existing.tasks.push(task)
                } else {
                  tasksByUserId.set(assignedToUser.id, { user: assignedToUser, tasks: [task] })
                }
              }
            }),
          )

          return { data: [...tasksByUserId.values()] }
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            } as FetchBaseQueryError,
          }
        }
      },
      providesTags: ['Tasks'],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetRoomQuery,
  useGetUserRoomSessionsQuery,
  useLazyGetRoomSessionsForUserQuery,
  useLazyGetRoomSessionsForFilterQuery,
  useGetRoomSessionsForFilterQuery,
  useDeleteRoomSessionMutation,
  useGetLiveStatusQuery,
  useGetPhoneCandidatesQuery,
  useGetClientsPreviousMeetingTasksQuery,
} = faApi
